/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Essential storage keys that are required for core application functionality.
 * These keys are only allowed when consent is PENDING (not yet given/rejected),
 * to allow users to log in before making a consent decision.
 * 
 * When consent is REJECTED, these keys should be cleared.
 * 
 * These include:
 * - OIDC authentication tokens and state (required for login)
 * - JWKS cache (required for token verification)
 */
const ESSENTIAL_STORAGE_KEY_PREFIXES = [
  'oidc:user',  // OIDC user data and tokens
  'oidc:state', // OIDC state management
] as const;

const ESSENTIAL_STORAGE_KEYS = [
  'jwks_datas', // JWKS cache for token verification
  'returnPath', // Return path for post-authentication redirect (part of auth flow)
] as const;

/**
 * Checks if a storage key is essential for core functionality.
 * Essential keys are only allowed when consent is PENDING (not rejected).
 *
 * @param {string} key - The storage key to check
 * @returns {boolean} True if the key is essential
 */
export function isEssentialStorageKey(key: string): boolean {
  // Check if key matches any essential prefix
  if (ESSENTIAL_STORAGE_KEY_PREFIXES.some(prefix => key.startsWith(prefix))) {
    return true;
  }

  // Check if key is in essential keys list
  if (ESSENTIAL_STORAGE_KEYS.includes(key as any)) {
    return true;
  }

  return false;
}
